import type { ApiUserAccount } from 'src/types/userAuth';

import { useSyncExternalStore } from 'react';

import { isTokenRejected } from 'src/utils/requestError';
import {
  sessionSnapshot,
  subscribeUserSession,
  SERVER_SESSION_SNAPSHOT,
} from 'src/utils/userSession';

import { useUserMe } from 'src/queries';

import { useHydrated } from './useHydrated';

export interface UserSession {
  /** The api confirmed a session for this browser: `/user/auth/me` answered with an account. */
  isLoggedIn: boolean;
  /** The account behind it (`/user/auth/me`), once the api has answered. */
  account: ApiUserAccount | undefined;
  /** This browser signed in here before, and the api has not said yet whether the session still stands. */
  isLoading: boolean;
  /**
   * The api has given its verdict on `/user/auth/me` for this tab's epoch — an account, or a refusal
   * (401/403) — for every browser, with the hint or without: until then a dealer who signed in in the
   * cabinet is not known to be one (app#397: whose the assistant's conversation is). A failure (a
   * 5xx, no network) is no verdict: who is here stays unknown.
   */
  isChecked: boolean;
  /** The api refused the session (401/403): there is none, whatever the hint says. */
  isRejected: boolean;
  /** This browser signed in here before — the hint in storage, not the session (api#454). */
  hasHint: boolean;
  /** The tab's epoch: one more at every sign-in and sign-out. What the cache hygiene keys on (`SessionProvider`). */
  epoch: number;
}

/**
 * The account session — the cookie the storefront and the dealer cabinet hold together since the
 * single sign-in (api#454) — as any screen reads it. No script sees the cookie, so the one way to
 * know is to ask: `/user/auth/me` is asked once the browser has the page, for everyone. A visitor
 * who never signed in here is drawn as a guest while it is asked and stays one on the 401; a
 * browser that signed in here before (the hint) waits for the answer instead, so a signed-in person
 * never sees the anonymous page for a frame; a dealer who signed in in the cabinet and opens the
 * storefront for the first time is a guest for the one frame it takes the api to say otherwise.
 *
 * A pure read: what follows from a refused session (the hint is dropped) and from a session that
 * ended (its reads leave the cache) is done once, in `SessionProvider`, not by every screen that
 * looks.
 *
 * Only a 401/403 is the api's verdict on the session. Any other failure — a 5xx, no network —
 * leaves the hint alone: the session was not judged, and the screen shows its own failure state or
 * the account it already had (app#207).
 */
export const useUserSession = (): UserSession => {
  const hydrated = useHydrated();
  const snapshot = useSyncExternalStore(
    subscribeUserSession,
    sessionSnapshot,
    () => SERVER_SESSION_SNAPSHOT
  );
  const [epochPart, hintPart] = snapshot.split(':');
  const epoch = Number(epochPart);
  const hasHint = hintPart === '1';

  // The heartbeat — `/me` re-asked on every return to the tab — is for a browser that signed in here
  // (the hint); a guest's tab switches ask nothing. The one question every visitor gets is the first.
  const {
    data: account,
    error,
    isPending,
  } = useUserMe(hydrated ? epoch : null, { heartbeat: hasHint });
  const isRejected = isTokenRejected(error);

  return {
    isLoggedIn: !!account && !isRejected,
    account: isRejected ? undefined : account,
    // A browser that signed in here before counts as loading until the api answers: the shell keeps
    // the splash up, so the screens mount once (app#156). A visitor with no hint waits for nothing.
    isLoading: hydrated && hasHint && isPending,
    isChecked: hydrated && !isPending && (!!account || isRejected),
    isRejected,
    hasHint,
    epoch,
  };
};
